import DeleteWarningIcon from '@mui/icons-material/WarningAmberRounded'
import { Alert, Autocomplete, Box, MenuItem, Typography, useTheme } from '@mui/material'
import { styled } from '@mui/material/styles'
import { useCallback, useEffect, useState } from 'react'
import { UseFormReturn } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { AppTextField } from 'src/components/AppTextField'
import { GenericDialogLayout } from 'src/components/GenericDialogLayout'
import { MainButton } from 'src/components/MainButton'
import { IMainComponent } from 'src/generated-types/main-component'
import { IMainComponentType } from 'src/generated-types/main-component-type'
import { useDialogCtx } from 'src/hooks/context-hooks'
import { string, z } from 'zod'
import { useGetCustomerLocalities } from '../../api/localities'
import { useGetCustomerSuppliers } from '../../api/supplier'
import { AppForm, AppFormProps, FormSubmitButton } from '../../components/FormCore'
import { APIResultCount } from '../../constant'
import { IMainComponentType as Type } from '../../enums'
import { ILocality } from '../../generated-types'
import { useZodForm } from '../../hooks/zod-form'
import { MainComponentCertificationAdd } from '../customer/mainComponents/MainComponentCertificationAdd'
import { SelectPlacement } from './SelectPlacement'

export interface SendToLocalityDialogFormProps extends Omit<AppFormProps, 'onSubmit'> {
  customerId: EntityId
  isCertificateAddFormVisible: boolean
  isPlacementVisible: boolean
  mainComponentTypeDetails?: {
    mainComponentType: IMainComponentType | undefined
    isCertificateDetailsRequired: boolean
  }
  setMainComponentTypeDetails?: React.Dispatch<
    React.SetStateAction<{
      mainComponentType: IMainComponentType | undefined
      isCertificateDetailsRequired: boolean
    }>
  >
  mainComponent?: IMainComponent
  onSubmit: (formData: any, formContext: UseFormReturn<any>) => void
}

export const SendToLocalityDialogForm: React.FC<SendToLocalityDialogFormProps> = ({
  onSubmit,
  onCancel,
  customerId,
  isCertificateAddFormVisible,
  isPlacementVisible,
  mainComponentTypeDetails,
  setMainComponentTypeDetails,
  mainComponent,
}) => {
  const { t } = useTranslation(['service-station', 'common'])
  const { showConfirmDialog } = useDialogCtx()
  const theme = useTheme()

  const MainComponentSchema = z.object({
    selectedLocalityId: z.number(),
    installationDate: z.date().or(z.string()).optional(),
    selectedPositionId: z.number().optional(),
    selectedMooringId: z.number().optional(),
    isForceAddedToLocality: z.boolean().optional().default(false),
    formSchemaReports: z.array(z.any()).optional(),
  })

  const MainComponentSchemaWithCertificate = z
    .object({
      certificateNumber: z
        .string()
        .nonempty()
        .regex(/^(?! *$).+/, t('certificateNumberIsInvalid', { ns: 'certificate' })),
      certificateDate: z.date().or(string()),
      validUntil: z.date().or(z.string()).optional(),
      certificateDocumentType: z.number(),
      certificateFileId: z.union([z.string(), z.number()]),
      certificateSupplierId: z
        .object({
          id: z.number(),
        })
        .transform(value => value.id),
      validPeriod: z
        .object({
          id: z.number(),
        })
        .transform(value => value.id),
    })
    .merge(MainComponentSchema)

  const { data } = useGetCustomerLocalities({ clientId: customerId })
  const [selectedLocality, setSelectedLocality] = useState<ILocality | undefined>()
  const [validationErrors, setValidationErrors] = useState<string[]>([])
  const { data: suppliers } = useGetCustomerSuppliers(customerId, 0, APIResultCount.Max)

  const form = useZodForm(
    mainComponentTypeDetails?.isCertificateDetailsRequired ? MainComponentSchemaWithCertificate : MainComponentSchema,
    { mode: 'onChange', reValidateMode: 'onChange' },
  )

  const validateMainComponentWithLocality = useCallback(
    (locality: ILocality) => {
      const equipmentErrors: string[] = []
      const localityErrors: string[] = []

      if (!mainComponent || mainComponent.type?.componentType !== Type.Net) return []

      const envValues = locality.solidityAndCircumferences || []
      const { circumference, solidity } = mainComponent

      // Equipment validations
      if (!circumference) {
        equipmentErrors.push(t('circumferenceIsRequired', { ns: 'main-component' }))
      }

      if (!solidity) {
        equipmentErrors.push(t('solidityIsRequired', { ns: 'main-component' }))
      }

      if (!mainComponent.mainComponentProperties || mainComponent.mainComponentProperties.length === 0) {
        equipmentErrors.push(t('noMainComponentProperties', { ns: 'main-component' }))
      }

      // Locality validations
      if (!envValues.length) {
        localityErrors.push(t('noLocalityCircumferenceOrSolidity', { ns: 'locality' }))
      }

      const localityHS = selectedLocality?.hs
      const localityVC5M = selectedLocality?.vC5M
      const localityVC15M = selectedLocality?.vC15M

      if (!localityHS && !localityVC5M && !localityVC15M) {
        localityErrors.push(t('noLocalityEnvironmentalValues', { ns: 'locality' }))
      }

      // Circumference and solidity validations against locality values
      if (circumference && solidity && envValues.length) {
        const hasValidSet = envValues.some(
          env => env.circumference === circumference && env.solidity !== undefined && env.solidity >= solidity,
        )

        if (!hasValidSet) {
          const allowedCircumferences = envValues
            .map(env => env.circumference)
            .filter(c => c !== null && c !== undefined)

          if (allowedCircumferences.length > 0 && !allowedCircumferences.includes(circumference as number)) {
            equipmentErrors.push(t('circumferenceNotMatched', { ns: 'main-component' }))
          }

          const envValue = envValues.find(env => env.circumference === circumference)
          if (
            envValue &&
            envValue.solidity !== null &&
            envValue.solidity !== undefined &&
            solidity &&
            envValue.solidity < solidity
          ) {
            equipmentErrors.push(t('solidityValueMustBeLowerThanLocality', { ns: 'main-component' }))
          }
        }
      }

      // Main component properties validations against locality values
      if (mainComponent.mainComponentProperties?.length) {
        const hasValidCombination = mainComponent.mainComponentProperties.some(mcp => {
          const hsValid = localityHS && mcp.hs >= localityHS
          const vc5Valid = localityVC5M && mcp.vC5 >= localityVC5M
          const vc15Valid = localityVC15M && mcp.vC15 ? mcp.vC15 >= localityVC15M : true
          return hsValid && vc5Valid && vc15Valid
        })

        if (!hasValidCombination) {
          const failingValues = new Set<string>()

          mainComponent.mainComponentProperties.forEach(mcp => {
            if (!localityHS || (mcp.hs && mcp.hs < localityHS)) {
              failingValues.add('HS')
            }
            if (localityVC5M && mcp.vC5 && mcp.vC5 < localityVC5M) {
              failingValues.add('VC5')
            }
            if (localityVC15M && mcp.vC15 && mcp.vC15 < localityVC15M) {
              failingValues.add('VC15')
            }
          })

          const failingValuesList = Array.from(failingValues)
          if (failingValuesList.length === 1) {
            equipmentErrors.push(
              t('singleValueMustBeGreaterThanLocality', { ns: 'main-component', value: failingValuesList[0] }),
            )
          } else if (failingValuesList.length > 1) {
            equipmentErrors.push(
              t('multipleValuesMustBeGreaterThanLocality', {
                ns: 'main-component',
                values: failingValuesList.join(' / '),
              }),
            )
          }
        }
      }

      return [...equipmentErrors, ...localityErrors]
    },
    [mainComponent, selectedLocality, t],
  )

  useEffect(() => {
    setValidationErrors(selectedLocality ? validateMainComponentWithLocality(selectedLocality) : [])
  }, [selectedLocality, mainComponent])

  const handleSendAnyway = async () => {
    const isValid = await form.trigger()

    const WarningContent = () => (
      <Box>
        <Typography variant="body1" sx={{ mb: 2 }}>
          {t('sendAnywayWarning', { ns: 'main-component' })}
        </Typography>
        <Box paddingX={'8px'}>
          <StyledAlert severity="warning" icon={false}>
            {validationErrors.map((error, index) => (
              <Typography
                key={index}
                fontWeight={400}
                textAlign={'left'}
                component="div"
                sx={{ display: 'flex', alignItems: 'flex-start' }}
              >
                <Box component="span" sx={{ mr: 1 }}>
                  •
                </Box>
                <Box component="span">{error}</Box>
              </Typography>
            ))}
          </StyledAlert>
        </Box>
      </Box>
    )

    if (isValid) {
      showConfirmDialog(
        t('areYouSure', { ns: 'common' }),
        <WarningContent />,
        {
          acceptText: t('yes', { ns: 'common' }),
        },
        async pop => {
          form.setValue('isForceAddedToLocality', true)
          await form.handleSubmit(data => onSubmit(data, form))()
          pop()
        },
        {
          Icon: (
            <DeleteWarningIcon
              sx={{ height: '43px', width: '48px', color: theme.palette.errorMain[theme.palette.mode] }}
            />
          ),
          iconBGColor: '#D32F2F4D',
          buttonTextColor: theme.palette.white[theme.palette.mode],
          buttonBGColor: theme.palette.errorMain[theme.palette.mode],
        },
      )
    }
  }
  return (
    <AppForm form={form} onSubmit={data => onSubmit(data, form)} hasInitialValues={false}>
      <GenericDialogLayout
        title={t('sendToLocality', { ns: 'main-component' })}
        pop={onCancel}
        actions={[
          validationErrors.length > 0 ? (
            <MainButton key="send-anyway" variant="contained" onClick={handleSendAnyway}>
              {t('sendAnyway', { ns: 'common' })}
            </MainButton>
          ) : (
            <FormSubmitButton key="send-to-locality" createText={t('Send', { ns: 'common' })} />
          ),
        ]}
        removedCloseButton={false}
      >
        <Autocomplete
          value={selectedLocality ?? null}
          onChange={(_, value) => {
            const loc = value as ILocality
            setSelectedLocality(loc ?? undefined)
            form.setValue('selectedLocalityId', loc?.id ?? 0)
          }}
          options={
            (data?.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })) ??
              []) as ILocality[]
          }
          autoHighlight
          getOptionLabel={o => o.name}
          renderOption={(props, o) => (
            <MenuItem {...props} key={o.id} value={o.id}>
              {o.name}
            </MenuItem>
          )}
          renderInput={params => (
            <AppTextField
              {...params}
              label={t('selectALocality', { ns: 'service-station' })}
              inputProps={{
                ...params.inputProps,
              }}
            />
          )}
          isOptionEqualToValue={(option, value) => option.id === value.id}
        />
        {validationErrors.length > 0 && (
          <Box paddingX={'8px'}>
            <StyledAlert severity="warning">
              {validationErrors.map((error, index) => (
                <Typography
                  key={index}
                  fontWeight={400}
                  component="div"
                  sx={{ display: 'flex', alignItems: 'flex-start' }}
                >
                  <Box component="span" sx={{ mr: 1 }}>
                    •
                  </Box>
                  <Box component="span">{error}</Box>
                </Typography>
              ))}
            </StyledAlert>
          </Box>
        )}
        {selectedLocality?.id && !isPlacementVisible && (
          <SelectPlacement
            customerId={customerId}
            localityId={selectedLocality.id}
            disabled={isPlacementVisible}
            form={form}
          />
        )}

        {isCertificateAddFormVisible && (
          <MainComponentCertificationAdd
            accordingSummary={'certificate'}
            form={form}
            suppliers={suppliers}
            isProductCertificateAdd={false}
            mainComponentTypeDetails={mainComponentTypeDetails}
            setMainComponentTypeDetails={setMainComponentTypeDetails}
          />
        )}
      </GenericDialogLayout>
    </AppForm>
  )
}

const StyledAlert = styled(Alert)(({ theme }) => ({
  '& .MuiAlert-icon': {
    alignItems: 'center',
    display: 'flex',
  },
}))
